import type {
  ColumnOrderState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  ChevronDown,
  Columns3,
  Layers,
  Plus,
  RefreshCw,
  Search,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useLocation } from '#router'
import { TABLE_STATE_KEYS } from '@/components/admin/shared/hooks/table-session-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  ColumnContextMenu,
  FilterDropdown,
  MultiSelectFilter,
  ResizableHeader,
} from '../problems/master-questions-table/components'
import { useSDColumnDefinitions } from './hooks/use-sd-column-definitions'
import { sdTableStore } from './hooks/use-sd-session-storage'
import { useSDTableFilters } from './hooks/use-sd-table-filters'
import type { SystemDesignProblem } from './hooks/use-system-design-problems'
import { useSystemDesignProblems } from './hooks/use-system-design-problems'

// =============================================================================
// Constants
// =============================================================================

const ITEMS_PER_PAGE = 50

const DIFFICULTY_OPTIONS = ['Easy', 'Medium', 'Hard'] as const

const SD_COLUMN_LABELS: Record<string, string> = {
  name: 'Name',
  difficulty: 'Difficulty',
  source: 'Source',
  companyTags: 'Company tags',
  topics: 'Topics',
  estimatedMinutes: 'Est. Min',
}

const SD_DEFAULT_COLUMN_ORDER: ColumnOrderState = [
  'name',
  'difficulty',
  'source',
  'companyTags',
  'topics',
  'estimatedMinutes',
  'delete',
]

const SD_DEFAULT_HIDDEN_COLUMNS: VisibilityState = {}

// =============================================================================
// Types
// =============================================================================

interface ColumnWrappingState {
  [columnId: string]: boolean
}

interface ContextMenuState {
  x: number
  y: number
  columnId: string
}

// =============================================================================
// Component
// =============================================================================

export function SystemDesignTable() {
  const { problems, loading, error, refresh, deleteProblem } =
    useSystemDesignProblems()
  const { navigate } = useLocation()

  // ---------------------------------------------------------------------------
  // Filters (from hook)
  // ---------------------------------------------------------------------------

  const {
    globalFilter,
    setGlobalFilter,
    difficultyFilter,
    setDifficultyFilter,
    sourceFilter,
    setSourceFilter,
    topicsFilter,
    setTopicsFilter,
    filteredProblems,
    allSources,
    allTopics,
    visibleCount,
    loadMore,
    globalFilterFn,
  } = useSDTableFilters({ problems, persist: true })

  // ---------------------------------------------------------------------------
  // Column definitions
  // ---------------------------------------------------------------------------

  const handleDeleteProblem = useCallback(
    async (id: string) => {
      try {
        await deleteProblem(id)
        toast.success('Problem deleted successfully')
      } catch {
        toast.error('Failed to delete problem')
      }
    },
    [deleteProblem],
  )

  const columns = useSDColumnDefinitions({
    onDeleteProblem: handleDeleteProblem,
    topicsFilter,
  })

  // ---------------------------------------------------------------------------
  // Table state (persisted to session storage)
  // ---------------------------------------------------------------------------

  const [sorting, setSorting] = useState<SortingState>(() =>
    sdTableStore.getValue<SortingState>(TABLE_STATE_KEYS.SORTING, []),
  )

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () =>
      sdTableStore.getValue<VisibilityState>(
        TABLE_STATE_KEYS.COLUMN_VISIBILITY,
        SD_DEFAULT_HIDDEN_COLUMNS,
      ),
  )

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() =>
    sdTableStore.getValue<ColumnOrderState>(
      TABLE_STATE_KEYS.COLUMN_ORDER,
      SD_DEFAULT_COLUMN_ORDER,
    ),
  )

  const [columnWrapping, setColumnWrapping] = useState<ColumnWrappingState>(
    () =>
      sdTableStore.getValue<ColumnWrappingState>(
        TABLE_STATE_KEYS.COLUMN_WRAPPING,
        {},
      ),
  )

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)

  // Column visibility dropdown state
  const [showColumnDropdown, setShowColumnDropdown] = useState(false)
  const columnDropdownRef = useRef<HTMLDivElement>(null)

  // ---------------------------------------------------------------------------
  // Persist state to session storage
  // ---------------------------------------------------------------------------

  useEffect(() => {
    sdTableStore.setValue(TABLE_STATE_KEYS.SORTING, sorting)
  }, [sorting])

  useEffect(() => {
    sdTableStore.setValue(TABLE_STATE_KEYS.COLUMN_VISIBILITY, columnVisibility)
  }, [columnVisibility])

  useEffect(() => {
    sdTableStore.setValue(TABLE_STATE_KEYS.COLUMN_ORDER, columnOrder)
  }, [columnOrder])

  useEffect(() => {
    sdTableStore.setValue(TABLE_STATE_KEYS.COLUMN_WRAPPING, columnWrapping)
  }, [columnWrapping])

  // ---------------------------------------------------------------------------
  // Close column dropdown on outside click
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        columnDropdownRef.current &&
        !columnDropdownRef.current.contains(e.target as Node)
      ) {
        setShowColumnDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // ---------------------------------------------------------------------------
  // TanStack Table instance
  // ---------------------------------------------------------------------------

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table API limitation
  const table = useReactTable({
    data: filteredProblems,
    columns,
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      columnOrder,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnResizing: true,
    columnResizeMode: 'onEnd',
  })

  // Get sizing info for smooth resize feedback
  const { columnSizingInfo } = table.getState()
  const isResizing = !!columnSizingInfo.isResizingColumn
  const deltaOffset = columnSizingInfo.deltaOffset ?? 0
  const tableWidth = isResizing
    ? table.getTotalSize() + deltaOffset
    : table.getTotalSize()

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleRowClick = useCallback(
    (problem: SystemDesignProblem) => {
      navigate(`/admin/system-design/${problem.id}`)
    },
    [navigate],
  )

  const handleAddProblem = useCallback(() => {
    navigate('/admin/system-design/new')
  }, [navigate])

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      if (columnId === 'delete') return
      e.preventDefault()
      setContextMenu({ x: e.clientX, y: e.clientY, columnId })
    },
    [],
  )

  const handleHideColumn = useCallback(() => {
    if (contextMenu) {
      setColumnVisibility((prev) => ({
        ...prev,
        [contextMenu.columnId]: false,
      }))
    }
  }, [contextMenu])

  const handleToggleWrap = useCallback(() => {
    if (contextMenu) {
      setColumnWrapping((prev) => ({
        ...prev,
        [contextMenu.columnId]: !prev[contextMenu.columnId],
      }))
    }
  }, [contextMenu])

  const handleToggleVisibility = (columnId: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: prev[columnId] === false,
    }))
  }

  const handleShowAll = () => {
    setColumnVisibility({})
  }

  const hasActiveFilters =
    !!globalFilter ||
    !!difficultyFilter ||
    !!sourceFilter ||
    topicsFilter.length > 0

  const handleClearAllFilters = useCallback(() => {
    setGlobalFilter('')
    setDifficultyFilter(null)
    setSourceFilter(null)
    setTopicsFilter([])
  }, [setGlobalFilter, setDifficultyFilter, setSourceFilter, setTopicsFilter])

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-3">
          <RefreshCw size={20} className="animate-spin" />
          <span>Loading system design problems...</span>
        </div>
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
        <div className="mx-auto w-full px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 p-2">
                <Layers size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-foreground text-xl font-bold">
                  System Design Problems
                </h1>
                <p className="text-muted-foreground text-sm">
                  {problems.length} total problems
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={handleAddProblem}>
                <Plus size={18} className="mr-2" />
                Add Problem
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto w-full px-4 py-6">
        {error ? (
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <Layers size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              Unable to load system design problems
            </h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={refresh} variant="outline">
              <RefreshCw size={18} className="mr-2" />
              Retry
            </Button>
          </div>
        ) : problems.length === 0 ? (
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <Layers size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              No system design problems yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Add your first system design problem to get started.
            </p>
            <div className="flex justify-center gap-2">
              <Button onClick={handleAddProblem}>
                <Plus size={18} className="mr-2" />
                Add Problem
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-card border-border overflow-hidden rounded-lg border p-4">
            {/* Filter Controls */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {/* Search input */}
              <div className="relative w-72">
                <Search
                  size={16}
                  className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                />
                <Input
                  type="text"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search problems..."
                  className="pl-9"
                />
                {globalFilter && (
                  <button
                    type="button"
                    onClick={() => setGlobalFilter('')}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Difficulty filter */}
              <FilterDropdown
                label="Difficulty"
                value={difficultyFilter}
                options={DIFFICULTY_OPTIONS}
                onChange={setDifficultyFilter}
              />

              {/* Source filter */}
              <FilterDropdown
                label="Source"
                value={sourceFilter}
                options={allSources}
                onChange={setSourceFilter}
              />

              {/* Topics multi-select filter */}
              <MultiSelectFilter
                label="Topics"
                selectedValues={topicsFilter}
                options={allTopics}
                onChange={setTopicsFilter}
                variant="violet"
              />

              {/* Clear all filters */}
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleClearAllFilters}
                  className="text-muted-foreground hover:text-foreground flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm transition-colors"
                >
                  <X size={14} />
                  Clear filters
                </button>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Column visibility dropdown */}
              <div ref={columnDropdownRef} className="relative">
                <button
                  type="button"
                  onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                  className="border-border text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors"
                >
                  <Columns3 size={14} />
                  Columns
                </button>
                {showColumnDropdown && (
                  <div className="bg-popover border-border absolute top-full right-0 z-30 mt-1 min-w-[180px] rounded-lg border py-1 shadow-xl">
                    {Object.entries(SD_COLUMN_LABELS).map(([id, label]) => (
                      <label
                        key={id}
                        className="hover:bg-muted flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={columnVisibility[id] !== false}
                          onChange={() => handleToggleVisibility(id)}
                          className="accent-primary h-3.5 w-3.5 rounded"
                        />
                        <span className="text-foreground">{label}</span>
                      </label>
                    ))}
                    <div className="border-border my-1 border-t" />
                    <button
                      type="button"
                      onClick={handleShowAll}
                      className="text-muted-foreground hover:bg-muted hover:text-foreground flex w-full items-center gap-2 px-3 py-1.5 text-sm transition-colors"
                    >
                      Show All
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Context Menu */}
            {contextMenu && (
              <ColumnContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                columnId={contextMenu.columnId}
                columnLabel={
                  SD_COLUMN_LABELS[contextMenu.columnId] || contextMenu.columnId
                }
                isWrapped={columnWrapping[contextMenu.columnId] ?? false}
                onHide={handleHideColumn}
                onToggleWrap={handleToggleWrap}
                onClose={() => setContextMenu(null)}
              />
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <table
                style={{
                  width: tableWidth,
                  minWidth: '100%',
                  tableLayout: 'fixed',
                }}
              >
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="border-border border-b">
                      {headerGroup.headers.map((header) => (
                        <ResizableHeader
                          key={header.id}
                          header={header}
                          columnSizingInfo={columnSizingInfo}
                          onContextMenu={handleContextMenu}
                        />
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="text-muted-foreground py-8 text-center"
                      >
                        No problems found.
                      </td>
                    </tr>
                  ) : (
                    table
                      .getRowModel()
                      .rows.slice(0, visibleCount)
                      .map((row) => (
                        <tr
                          key={row.id}
                          onClick={() => handleRowClick(row.original)}
                          className="border-border hover:bg-muted/50 cursor-pointer border-b transition-colors"
                        >
                          {row.getVisibleCells().map((cell) => {
                            const isCellResizing =
                              columnSizingInfo.isResizingColumn ===
                              cell.column.id
                            const cellWidth = isCellResizing
                              ? cell.column.getSize() + deltaOffset
                              : cell.column.getSize()
                            const isWrapped =
                              columnWrapping[cell.column.id] ?? false
                            return (
                              <td
                                key={cell.id}
                                style={{ width: cellWidth }}
                                className={
                                  isWrapped
                                    ? 'overflow-hidden px-3 py-2 break-words whitespace-normal'
                                    : 'overflow-hidden px-3 py-2 text-ellipsis whitespace-nowrap'
                                }
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Results count and Load More */}
            <div className="mt-3 flex flex-col items-center gap-3">
              <div className="text-muted-foreground text-xs">
                Showing{' '}
                {Math.min(visibleCount, table.getRowModel().rows.length)} of{' '}
                {table.getRowModel().rows.length} problems
                {table.getRowModel().rows.length !== problems.length &&
                  ` (filtered from ${problems.length})`}
                {globalFilter && ` matching "${globalFilter}"`}
                {difficultyFilter && ` \u2022 ${difficultyFilter}`}
                {sourceFilter && ` \u2022 ${sourceFilter}`}
                {topicsFilter.length > 0 &&
                  ` \u2022 Topics: ${topicsFilter.join(', ')}`}
              </div>

              {visibleCount < table.getRowModel().rows.length && (
                <button
                  type="button"
                  onClick={loadMore}
                  className="border-border bg-muted hover:bg-muted/80 text-foreground flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
                >
                  <ChevronDown size={16} />
                  Load More (
                  {Math.min(
                    ITEMS_PER_PAGE,
                    table.getRowModel().rows.length - visibleCount,
                  )}{' '}
                  more)
                </button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
